/* import __COLOCATED_TEMPLATE__ from './sla-dependency-modal-base.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import ajax from 'embercom/lib/ajax';
import { inject as service } from '@ember/service';
import { OUTBOUND_LIST_ROUTES } from 'embercom/models/outbound/content-wrapper';
import type IntlService from 'ember-intl/services/intl';

interface DependentObject {
  id: string;
  name?: string;
  title?: string;
}

interface DependencyResponse {
  dependent_sla_rules?: DependentObject[];
  dependent_workflows?: DependentObject[];
  dependent_custom_bots?: DependentObject[];
  dependent_answers?: DependentObject[];
  dependent_behaviors?: DependentObject[];
}

interface Sla {
  id: string;
  name?: string;
}

interface SlaDependencyModalBaseSignature {
  Args: {
    sla: Sla;
    onClose: () => void;
    action: $TSFixMe;
    actionType: 'archive' | 'delete';
    blockedMessage: string;
    confirmMessage: string;
    actionButtonLabel: string;
  };
}

export default class SlaDependencyModalBase extends Component<SlaDependencyModalBaseSignature> {
  @service declare appService: $TSFixMe;
  @service declare store: $TSFixMe;
  @service declare intl: IntlService;
  @tracked dependentRules: DependentObject[] = [];
  @tracked dependentWorkflows: DependentObject[] = [];
  @tracked dependentCustomBots: DependentObject[] = [];
  @tracked dependentAnswers: DependentObject[] = [];
  @tracked dependentBehaviors: DependentObject[] = [];

  constructor(owner: unknown, args: SlaDependencyModalBaseSignature['Args']) {
    super(owner, args);
    taskFor(this.checkForDependents).perform(this.args.sla);
  }

  get app() {
    return this.appService.app;
  }

  get inboxRulesLink() {
    return 'apps.app.settings.inbox-rules.edit';
  }

  get customBotsLink() {
    return OUTBOUND_LIST_ROUTES['custom_bot'];
  }

  get dependentObjects(): DependentObject[] {
    return [
      ...this.dependentRules,
      ...this.dependentWorkflows,
      ...this.dependentCustomBots,
      ...this.dependentAnswers,
      ...this.dependentBehaviors,
    ];
  }

  get hasDependentObjects(): boolean {
    return this.dependentObjects.length > 0;
  }

  get isCheckingDependencies(): boolean {
    return taskFor(this.checkForDependents).isRunning;
  }

  get actionType(): 'archive' | 'delete' {
    return this.args.actionType;
  }

  get loadingMessage(): string {
    return this.intl.t('settings.slas.dependency-modal.loading');
  }

  get blockedTitle(): string {
    return this.intl.t(`settings.slas.dependency-modal.blocked-title.${this.actionType}`, {
      slaName: this.args.sla?.name,
    });
  }

  get confirmTitle(): string {
    return this.intl.t(`settings.slas.dependency-modal.confirm-title.${this.actionType}`, {
      slaName: this.args.sla?.name,
    });
  }

  get title(): string {
    if (this.isCheckingDependencies) {
      return this.loadingMessage;
    }

    if (this.hasDependentObjects) {
      return this.blockedTitle;
    }

    return this.confirmTitle;
  }

  @task({ drop: true })
  *checkForDependents(sla: Sla) {
    try {
      let response: DependencyResponse = yield ajax({
        url: `/ember/inbox/conversation_slas/${sla.id}/dependent_objects`,
        type: 'GET',
        data: {
          app_id: this.app.id,
        },
      });

      let {
        dependent_sla_rules,
        dependent_workflows,
        dependent_custom_bots,
        dependent_answers,
        dependent_behaviors,
      } = response;
      this.dependentRules = dependent_sla_rules ?? [];
      this.dependentWorkflows = dependent_workflows ?? [];
      this.dependentCustomBots = dependent_custom_bots ?? [];
      this.dependentAnswers = dependent_answers ?? [];
      this.dependentBehaviors = dependent_behaviors ?? [];
    } catch (error) {
      console.error('Error checking SLA dependencies:', error);
      this.dependentRules = [];
      this.dependentWorkflows = [];
      this.dependentCustomBots = [];
      this.dependentAnswers = [];
      this.dependentBehaviors = [];
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Settings::SlaDependencyModalBase': typeof SlaDependencyModalBase;
    'settings/sla-dependency-modal-base': typeof SlaDependencyModalBase;
  }
}
